import { useIsNextGenZapsEntitled, useSplitTreatments } from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import { Icon, IconName } from "@zapier/zinnia-icons";
import { BetaSecondaryNavItem } from "@zapier/design-system";
import { AGENTS_UI_BASE_URL, getNextGenZapsIndexUrl } from "@zapier/utils";
import { usePathname } from "next/navigation";
import { useMemo } from "react";

import styles from "./AssetLinks.module.css";
import { TRASH_HREF } from "./trashHref";

export interface AssetLink {
  href: string;
  icon: IconName;
  isMobileOnly?: boolean;
  text: string;
}

export function useAssetLinks() {
  const pathname = usePathname() || "";
  const {
    isAgentsListPageInAssetManagement,
    isZapAndCanvasUnifiedTrashEnabled,
  } = useSplitTreatments();
  const { isNextGenZapsEntitled } = useIsNextGenZapsEntitled();
  const t = useTranslations("navigation");

  const assetLinks: AssetLink[] = useMemo(() => {
    const nextGenZapsLink: AssetLink = {
      href: getNextGenZapsIndexUrl(),
      icon: "miscTriggerFill",
      text: t("next-gen-zaps"),
    };

    return [
      {
        href: "/app/assets/folders",
        icon: "navFolder",
        isMobileOnly: true,
        text: t("folders"),
      },
      {
        href: "/app/assets/favorites",
        icon: "ratingStar",
        isMobileOnly: true,
        text: t("favorites"),
      },
      {
        href: "/app/assets/zaps",
        icon: "miscBoltAltFill",
        text: t("zaps"),
      },
      ...(isNextGenZapsEntitled ? [nextGenZapsLink] : []),
      {
        href: "/app/assets/tables",
        icon: "navTables",
        text: t("tables"),
      },
      {
        href: "/app/assets/forms",
        icon: "navInterfaces",
        text: t("forms"),
      },
      {
        href: "/app/assets/chatbots",
        icon: "navAIChatbot",
        text: t("chatbots"),
      },
      {
        href: "/app/assets/canvases",
        icon: "navCanvas",
        text: t("canvases"),
      },
      {
        href: isAgentsListPageInAssetManagement
          ? "/app/assets/agents"
          : AGENTS_UI_BASE_URL,
        icon: "navAgents",
        text: t("agents"),
      },
      // Sidebar TrashLink is hidden below --desktop, so this is the only
      // Trash entry point on mobile/tablet once unified trash is on.
      ...(isZapAndCanvasUnifiedTrashEnabled
        ? [
            {
              href: TRASH_HREF,
              icon: "actionTrash" as IconName,
              isMobileOnly: true,
              text: t("trash"),
            },
          ]
        : []),
    ];
  }, [
    isAgentsListPageInAssetManagement,
    isNextGenZapsEntitled,
    isZapAndCanvasUnifiedTrashEnabled,
    t,
  ]);

  const desktopLinks = useMemo(
    () =>
      assetLinks.flatMap((item) => {
        if (item.isMobileOnly === true) {
          return [];
        }

        return (
          <BetaSecondaryNavItem
            key={item.href}
            className={styles.icon}
            label={
              <>
                <Icon name={item.icon} size={20} color="GrayWarm6" />
                {item.text}
              </>
            }
            linkProps={{ href: item.href }}
            itemKey={item.href}
          />
        );
      }),
    [assetLinks],
  );

  const mobileLinks = useMemo(
    () =>
      assetLinks.map((link) => ({
        href: link.href,
        text: (
          <div className={styles.mobileLink}>
            <Icon name={link.icon} size={16} /> {link.text}
          </div>
        ),
      })),
    [assetLinks],
  );

  const selectedHref = useMemo(() => {
    // Pick the longest (most specific) matching href, since e.g. the Trash
    // route is nested under the Folders href and would otherwise lose to it.
    const match = [...assetLinks]
      .sort((a, b) => b.href.length - a.href.length)
      .find((link) => pathname.startsWith(link.href));
    return match?.href ?? "";
  }, [assetLinks, pathname]);

  return {
    desktopLinks,
    mobileLinks,
    selectedHref,
  };
}
